import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate, useLocation } from 'react-router';
import env from '@jwp/ott-common/src/env';
import {
  GET_MEDIA_APPROVAL_BY_ID,
  GetMediaApprovalByIdData,
  GetMediaApprovalByIdVariables,
} from '@jwp/ott-common/src/graphql/query/moderate.queries';
import {
  APPROVE_OR_REJECT_MEDIA_APPROVAL,
  ApproveOrRejectMediaApprovalData,
  ApproveOrRejectMediaApprovalVariables,
} from '@jwp/ott-common/src/graphql/mutation/moderate.mutation';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { PATH_VIDEO_PREVIEW_WITH_ID } from '@jwp/ott-common/src/paths';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';

import Button from '../Button/Button';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import UploadBox from '../UploadBox/UploadBox';

import styles from './ReelReviewModal.module.scss';

type Props = {
  maId: string;
  viewOnly?: boolean;
};

const ReelReviewModal: React.FC<Props> = ({ maId, viewOnly = false }) => {
  const { t } = useTranslation(['video', 'common', 'upload']);
  const navigate = useNavigate();
  const location = useLocation();
  const setReturnPath = useUploadStore((s) => s.setReturnPath);

  const { data, loading, error } = useQuery<GetMediaApprovalByIdData, GetMediaApprovalByIdVariables>(GET_MEDIA_APPROVAL_BY_ID, {
    variables: { maId },
    skip: !maId,
  });

  const [approveOrReject, { loading: mutationLoading }] = useMutation<ApproveOrRejectMediaApprovalData, ApproveOrRejectMediaApprovalVariables>(
    APPROVE_OR_REJECT_MEDIA_APPROVAL,
    {
      onCompleted: () => {
        navigate(createURLFromLocation(location, { u: 'approval-success', id: null }));
      },
      refetchQueries: ['getAllMediaApprovalsList'],
    },
  );

  const handleApprove = () => {
    approveOrReject({
      variables: {
        input: {
          maId,
          maStatus: 'APPROVED',
        },
      },
    });
  };

  const handleReject = () => {
    navigate(createURLFromLocation(location, { u: 'reject-reason' }));
  };

  const media = data?.mediaApprovalByMaId;
  const videoUrl = media ? env.APP_BASE_URL + media.maS3MediaKeyUrl : '';
  const posterUrl = media ? env.APP_BASE_URL + media.maS3ThumbnailKeyUrl : '';

  const renderModalContent = () => {
    if (loading) {
      return (
        <div className={styles.loadingWrapper}>
          <LoadingOverlay inline />
        </div>
      );
    }

    if (error || !media) {
      return <div className={styles.errorWrapper}>{t('common:error_loading_data')}</div>;
    }

    return (
      <>
        <div className={styles.content}>
          <div className={styles.uploadColumn}>
            <UploadBox
              selectedFile={null}
              onFileSelect={() => {}}
              poster={posterUrl}
              previewUrl={videoUrl}
              type="reel"
              height={427}
              disabled={true}
              duration={media.maDuration ? Number(media.maDuration) : null}
              onPreview={() => {
                useUploadStore.getState().setInitiated(true);
                setReturnPath(`${location.pathname}${location.search}`);
                navigate(`${PATH_VIDEO_PREVIEW_WITH_ID.replace(':id', media.maId)}?returnPath=${encodeURIComponent(location.pathname + location.search)}`);
              }}
            />
          </div>

          <div className={styles.infoSection}>
            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:reel_review.form.title_label')}</span>
              <p>{media.maTitle || '-'}</p>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:reel_review.form.description_label')}</span>
              <p>{media.maDescription || '-'}</p>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:reel_review.form.category_label')}</span>
              <div className={styles.categoryBadgeWrapper}>
                <span className={styles.categoryBadge}>{media.maCategory || '-'}</span>
              </div>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:reel_review.form.tags_label')}</span>
              <p>
                {(() => {
                  const rawTags = media.maTags || [];
                  let tags = typeof rawTags === 'string' ? [rawTags] : rawTags;

                  // Handle double-serialized JSON strings within the array
                  if (rawTags.length === 1 && typeof rawTags[0] === 'string' && rawTags[0].startsWith('[')) {
                    try {
                      const parsed = JSON.parse(rawTags[0]);
                      if (Array.isArray(parsed)) {
                        tags = parsed;
                      }
                    } catch (e) {
                      // Fallback to raw tags if parsing fails
                      console.error('Failed to parse tags:', e);
                    }
                  }

                  return tags.length > 0 ? tags.map((tag) => (tag.trim().startsWith('#') ? tag.trim() : `#${tag.trim()}`)).join(', ') : '-';
                })()}
              </p>
            </div>

            <div className={styles.infoGroup}>
              <span className={styles.label}>{t('upload:reel_review.form.creator_label')}</span>
              <div className={styles.creatorRow}>
                <img src={`https://ui-avatars.com/api/?name=${media.userByMaUploadedBy?.uFullName || 'User'}`} alt="Creator" className={styles.avatar} />
                <span className={styles.name}>{media.userByMaUploadedBy?.uFullName || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        {!viewOnly && (
          <div className={styles.submitContainer}>
            <div className={styles.actions}>
              <Button label={t('upload:reel_review.form.reject_button')} variant="transparent" color='delete' className={styles.rejectBtn} onClick={handleReject} disabled={mutationLoading} />
              <Button label={t('upload:reel_review.form.approve_button')} color="primary" className={styles.approveBtn} onClick={handleApprove} busy={mutationLoading} />
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{viewOnly ? t('upload:reel_review.details_title') : t('upload:reel_review.title')}</h2>
      {renderModalContent()}
    </div>
  );
};

export default ReelReviewModal;


